import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { formatCurrency } from 'src/utils/formatters';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import useDemoInvoices, {
  InvoiceDTO,
} from '@/hooks/accounts-payable/useDemoInvoices';
import { InvoiceDetailSlidein } from '@/components/accounts-payable/invoice-detail/invoice-detail-slidein';
import { formatDateOnlyString } from '@/utils/dateUtils';

// Helper function to generate potential errors for demo
const generatePotentialErrors = (invoice: InvoiceDTO): string[] => {
  const errors: string[] = [];

  // Check for missing required fields
  if (!invoice.invoiceNumber) {
    errors.push('Missing invoice number');
  }

  if (!invoice.vendorName) {
    errors.push('Missing vendor name');
  }

  if (!invoice.totalAmount || invoice.totalAmount <= 0) {
    errors.push('Invalid total amount');
  }

  // Check for potential data quality issues
  // Note: OCR confidence not available in current schema

  // Check for duplicate invoice numbers (simplified demo logic)
  if (invoice.invoiceNumber && invoice.invoiceNumber.includes('DUP')) {
    errors.push('Potential duplicate');
  }

  // Check for unusual amounts - removed approval requirement

  // Check for missing PO
  if (!invoice.documentId) {
    errors.push('No supporting document');
  }

  // Include any raw data errors from the backend
  if (invoice.rawData?.potentialErrors) {
    errors.push(...invoice.rawData.potentialErrors);
  }

  return errors;
};

// Helper function to render error status
const renderErrorStatus = (invoice: InvoiceDTO) => {
  const errors = generatePotentialErrors(invoice);

  if (errors.length === 0) {
    return (
      <div className="flex items-center gap-1 text-green-600">
        <CheckCircleIcon className="h-4 w-4" />
        <span className="text-xs">Valid</span>
      </div>
    );
  }

  if (
    errors.some(
      (error) =>
        error.includes('Missing') ||
        error.includes('Invalid') ||
        error.includes('duplicate'),
    )
  ) {
    return (
      <div className="flex items-center gap-1 text-red-600">
        <XCircleIcon className="h-4 w-4" />
        <Badge
          variant="destructive"
          className="text-xs text-white bg-red-600 hover:bg-red-700"
        >
          {errors.length} Error{errors.length > 1 ? 's' : ''}
        </Badge>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-amber-600">
      <WarningIcon className="h-4 w-4" />
      <Badge
        variant="secondary"
        className="text-xs text-amber-800 bg-amber-100 hover:bg-amber-200"
      >
        {errors.length} Warning{errors.length > 1 ? 's' : ''}
      </Badge>
    </div>
  );
};

export function InvoiceDemosPage() {
  const { data, isLoading, error } = useDemoInvoices();
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<
    string | undefined
  >(undefined);
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  console.log({ data });

  const invoices = data?.invoices;

  const handleRowClick = (invoice: InvoiceDTO) => {
    setSelectedInvoiceId(invoice.id);
    setIsSliderOpen(true);
  };

  const handleSliderClose = () => {
    setIsSliderOpen(false);
    setSelectedInvoiceId(undefined);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading invoices</div>;
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoice Demos (POC)</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices?.map((invoice: InvoiceDTO) => (
                <TableRow
                  key={invoice.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(invoice)}
                >
                  <TableCell>{invoice.invoiceNumber || 'N/A'}</TableCell>
                  <TableCell>{invoice.vendorName || 'N/A'}</TableCell>
                  <TableCell>
                    {formatDateOnlyString(invoice.invoiceDate)}
                  </TableCell>
                  <TableCell>
                    {invoice.totalAmount
                      ? formatCurrency(invoice.totalAmount, {
                          currency: invoice.currency,
                        })
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{renderErrorStatus(invoice)}</TableCell>
                </TableRow>
              ))}
              {(!invoices || invoices.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    No invoices found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InvoiceDetailSlidein
        isOpen={isSliderOpen}
        onOpenChange={handleSliderClose}
        invoiceId={selectedInvoiceId}
      />
    </div>
  );
}
